---
title: "Asking Choosie questions"
description: "Use Choosie to search candidates, pull up a record, or check your pipeline. Read requests run immediately, stream as they go, and link straight to records."
category: "hq"
keywords:
  - "ask Choosie a question"
  - "search candidates with AI"
  - "Choosie candidate lookup"
  - "AI pipeline questions"
schema: "HowTo"
nav:
  section: "choosie"
  order: 10
  parent: "/choosie"
---

Asking [Choosie](/choosie) a question is the no-risk half of using it. Searches, lookups, and pipeline questions are *reads* — they don't change anything — so Choosie just runs them and answers. There's no plan to approve and nothing to undo. Type a question, get an answer.

## What counts as a question

Anything that looks something up rather than changes it. Choosie runs these the moment you ask:

- **Find candidates.** "Senior backend engineers in Berlin who applied this month." Choosie interprets the sentence and searches — the same engine behind [smart search](/guides/search/smart-vs-deep), reachable from chat.
- **Pull up one record.** "Show me Priya Nair's profile" or "what's the status on the Staff Designer role." Choosie fetches the candidate or job and summarizes it.
- **Read the history on someone.** Their notes, their feedback, the jobs they've applied to, the stage each application sits at.
- **Check the pipeline.** "How many candidates are in interview across all open roles." Choosie pulls the [pipeline summary](/guides/analytics/pipeline-and-sources) — counts by stage and by job.
- **Look at scheduling.** This week's interviews, what's still unscheduled, the AI scheduling events waiting for review.

## How an answer arrives

<Steps>
  <li>You type a question and send it.</li>
  <li>Choosie picks the right tool — looking up one named candidate is a different tool than searching a list — and runs it. You see each step it takes.</li>
  <li>The answer **streams** in as Choosie writes it, so you're reading before it's finished thinking.</li>
  <li>When it's done, any candidate or job Choosie named in the answer becomes a clickable link straight to that record.</li>
</Steps>

Those links are the fast path. Ask "who's in the offer stage for the Backend role," and instead of a list of names you have a list of links — click one, you're on the profile.

<Callout type="info" title="Choosie only sees what you see">
Choosie runs every search and lookup as you, with your role and inside your organization. It can't surface a candidate your account couldn't already open. If a question comes back with less than you expected, a [permission boundary](/getting-started/accounts-and-roles) is one thing to check.
</Callout>

## A conversation remembers itself

A chat keeps its context. Ask Choosie to find a candidate, then ask "what jobs has she applied to" — it knows who "she" is. You can narrow a search across several messages without repeating yourself.

This works best when one chat covers one task. A conversation that wanders from a candidate search into scheduling into a job edit gets long and unfocused, and Choosie has more to wade through each turn. For a new task, start a new chat — it's a click, and the old one is saved.

## When it gets it wrong

Choosie reads your question and decides what to search for. A vague question produces a vague search. "Show me good candidates" gives Choosie nothing concrete to filter on; "show me candidates with 5+ years of React who applied to the Frontend role" gives it real criteria. [Writing good prompts](/choosie/good-prompts) covers this.

A search that returns nothing usually isn't broken — the filter was just too tight, or the data isn't there. [Why a search returns no results](/guides/search/why-no-results) applies to Choosie's searches too. And Choosie summarizes what the tools return; for the full record with every field and document, open the profile. The link is right there in the answer.
